import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import { formatDate } from '../../../utils/formatUtils';

const COMPANY_COLORS = {
  headerBg: '5e894f',
  headerFont: 'FFFFFF',
  evenRow: 'E3E6F3',
  oddRow: 'FFFFFF',
  positiveGreen: '4caf50',
  negativeRed: 'f44336',
};

export async function exportInvoicesToExcel(
  invoices,
  contractInfo,
  filename = 'Facturas_Contrato.xlsx'
) {
  if (!invoices || invoices.length === 0) return;

  const workbook = new ExcelJS.Workbook();
  const worksheet = workbook.addWorksheet('Facturas');

  const columns = [
    { header: 'Nro. Comprobante', key: 'receiptNumber', width: 25 },
    { header: 'Fecha de Emisión', key: 'entryDate', width: 18 },
    { header: 'Monto Neto', key: 'toNet', width: 15 },
    { header: 'IVA', key: 'toIva', width: 15 },
    { header: 'Monto Total', key: 'toTotal', width: 15 },
  ];

  worksheet.columns = columns;

  const titleRow = worksheet.insertRow(1, [`Facturas del Contrato - ${contractInfo?.contract_number || 'N/A'}`]);
  titleRow.getCell(1).font = { bold: true, size: 14 };
  worksheet.mergeCells('A1:E1');
  titleRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
  titleRow.height = 25;

  if (contractInfo) {
    const infoRow = worksheet.insertRow(2, [
      `Contratista: ${contractInfo.subscriber_name || 'N/A'} | Plan: ${contractInfo.plan_name || 'N/A'}`
    ]);
    infoRow.getCell(1).font = { size: 11 };
    worksheet.mergeCells('A2:E2');
    infoRow.getCell(1).alignment = { vertical: 'middle', horizontal: 'center' };
    infoRow.height = 20;
  }

  worksheet.addRow([]);

  const headerRow = worksheet.addRow(columns.map(col => col.header));
  headerRow.eachCell((cell) => {
    cell.fill = {
      type: 'pattern',
      pattern: 'solid',
      fgColor: { argb: COMPANY_COLORS.headerBg },
    };
    cell.font = {
      color: { argb: COMPANY_COLORS.headerFont },
      bold: true,
      size: 11,
    };
    cell.alignment = { vertical: 'middle', horizontal: 'center' };
    cell.border = {
      top: { style: 'thin' },
      left: { style: 'thin' },
      bottom: { style: 'thin' },
      right: { style: 'thin' },
    };
  });

  const getReceiptNumber = (invoice) => {
    const typeLetter = invoice.type_letter || '-';
    const salePoint = invoice.sale_point_number
      ? String(invoice.sale_point_number).padStart(4, '0')
      : '----';
    const receiptNumber =
      invoice.afip_number && invoice.afip_number !== 0
        ? String(invoice.afip_number).padStart(8, '0')
        : invoice.receipt_number
        ? String(invoice.receipt_number).padStart(8, '0')
        : '--------';

    return `${typeLetter}-${salePoint}-${receiptNumber}`;
  };

  invoices.forEach((invoice, idx) => {
    const rowData = {
      receiptNumber: getReceiptNumber(invoice),
      entryDate: formatDate(invoice.entry_date),
      toNet: parseFloat(invoice.to_net) || 0,
      toIva: parseFloat(invoice.to_iva) || 0,
      toTotal: parseFloat(invoice.to_total) || 0,
    };

    const row = worksheet.addRow(rowData);

    row.eachCell((cell, colNumber) => {
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: idx % 2 === 0 ? COMPANY_COLORS.evenRow : COMPANY_COLORS.oddRow },
      };

      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };

      if (colNumber >= 3 && colNumber <= 5) {
        cell.numFmt = '[$$-es-AR] #,##0.00';
      }
    });
  });

  if (invoices.length > 0) {
    const totalNet = invoices.reduce((sum, inv) => sum + (parseFloat(inv.to_net) || 0), 0);
    const totalIva = invoices.reduce((sum, inv) => sum + (parseFloat(inv.to_iva) || 0), 0);
    const totalTotal = invoices.reduce((sum, inv) => sum + (parseFloat(inv.to_total) || 0), 0);

    const totalsRow = worksheet.addRow({
      receiptNumber: '',
      entryDate: 'TOTALES:',
      toNet: totalNet,
      toIva: totalIva,
      toTotal: totalTotal,
    });

    totalsRow.eachCell((cell, colNumber) => {
      cell.font = { bold: true };
      cell.fill = {
        type: 'pattern',
        pattern: 'solid',
        fgColor: { argb: COMPANY_COLORS.headerBg },
      };
      if (colNumber === 2) {
        cell.font.color = { argb: COMPANY_COLORS.headerFont };
      }
      if (colNumber >= 3) {
        cell.numFmt = '[$$-es-AR] #,##0.00';
        cell.font.color = { argb: COMPANY_COLORS.headerFont };
      }
      cell.alignment = { vertical: 'middle', horizontal: 'center' };
      cell.border = {
        top: { style: 'thin' },
        bottom: { style: 'thin' },
        left: { style: 'thin' },
        right: { style: 'thin' },
      };
    });
  }

  const buffer = await workbook.xlsx.writeBuffer();
  saveAs(
    new Blob([buffer], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    }),
    filename
  );
}
